import { db } from '@/db/db';
import { put, putMany, tombstoneMany } from '@/db/repo';
import { nowMs } from '@/db/clock';
import type {
  Constant,
  Formula,
  Item,
  ProblemTemplate,
  Session,
} from '@/domain/types';
import type { FullSessionInput, ValidatedBlock } from './schema';

export type ImportDecision = 'create' | 'replace' | 'merge' | 'keep-both' | 'append';

export interface ImportDiff {
  added: number;
  edited: number;
  removed: number;
}

export interface ImportPlan {
  block: ValidatedBlock;
  sessionId: string;
  /** What will happen if the user confirms without choosing anything else. */
  action: 'create' | 'append' | 'conflict';
  /** Only for `conflict`. */
  diff?: ImportDiff;
  existingTitle?: string;
  /** Set when the block cannot be applied at all. */
  blocker?: string;
  counts: { flashcards: number; questions: number; templates: number; formulas: number; constants: number };
}

export interface ApplyResult {
  sessionId: string;
  decision: ImportDecision;
  itemsWritten: number;
  itemsRemoved: number;
  templatesWritten: number;
  referenceWritten: number;
}

// ------------------------------------------------------------------ planning

/**
 * Plans a whole paste in order. `pending` carries the sessions that earlier
 * blocks in the same paste will create, so pasting part 1 and part 2 together
 * does not report part 2 as an orphan.
 */
export async function planBlocks(blocks: ValidatedBlock[]): Promise<ImportPlan[]> {
  const pending = new Set<string>();
  const plans: ImportPlan[] = [];
  for (const block of blocks) {
    const plan = await planBlock(block, pending);
    plans.push(plan);
    if (!plan.blocker && block.isFirstPart) pending.add(block.sessionId);
  }
  return plans;
}

export async function planBlock(
  block: ValidatedBlock,
  pending: ReadonlySet<string> = new Set(),
): Promise<ImportPlan> {
  const counts = {
    flashcards: block.flashcards.length,
    questions: block.questions.length,
    templates: block.problemTemplates.length,
    formulas: block.formulas.length,
    constants: block.constants.length,
  };
  const existing = await db.sessions.get(block.sessionId);
  const live = existing && !existing.deleted ? existing : undefined;

  if (!block.isFirstPart) {
    if (!live && pending.has(block.sessionId)) {
      return { block, sessionId: block.sessionId, action: 'append', counts };
    }
    if (!live) {
      return {
        block,
        sessionId: block.sessionId,
        action: 'append',
        counts,
        blocker: `This is part ${block.part.index} of ${block.part.of}, but part 1 for session "${block.sessionId}" has not been imported yet. Import part 1 first.`,
      };
    }
    return { block, sessionId: block.sessionId, action: 'append', counts, existingTitle: live.title };
  }

  if (!live) {
    return { block, sessionId: block.sessionId, action: 'create', counts };
  }

  return {
    block,
    sessionId: block.sessionId,
    action: 'conflict',
    diff: await diffAgainstExisting(block),
    existingTitle: live.title,
    counts,
  };
}

/** "12 new, 3 edited, 0 removed" — computed against what is stored today. */
export async function diffAgainstExisting(block: ValidatedBlock): Promise<ImportDiff> {
  const existingItems = (await db.items.where('sessionId').equals(block.sessionId).toArray()).filter(
    (i) => !i.deleted,
  );
  const existingById = new Map(existingItems.map((i) => [i.id, i]));

  const incoming = buildItems(block, block.sessionId, '', 0);
  const incomingIds = new Set(incoming.map((i) => i.id));

  let added = 0;
  let edited = 0;
  for (const item of incoming) {
    const current = existingById.get(item.id);
    if (!current) added += 1;
    else if (!sameContent(current, item)) edited += 1;
  }
  const removed = existingItems.filter((i) => !incomingIds.has(i.id)).length;
  return { added, edited, removed };
}

type ItemContent = Omit<Item, 'updatedAt'>;

function sameContent(a: ItemContent, b: ItemContent): boolean {
  return (
    a.type === b.type &&
    a.category === b.category &&
    a.difficulty === b.difficulty &&
    a.front === b.front &&
    a.back === b.back &&
    a.q === b.q &&
    a.sample === b.sample &&
    a.note === b.note &&
    a.flag === b.flag &&
    a.flagText === b.flagText &&
    a.answerIndex === b.answerIndex &&
    JSON.stringify(a.opts ?? null) === JSON.stringify(b.opts ?? null)
  );
}

// ------------------------------------------------------------------ building

type Draft<T> = Omit<T, 'updatedAt'>;

/**
 * Flattens a block into item records. `suffix` is appended to every id for
 * Keep both, so nothing collides with the session already stored.
 */
export function buildItems(
  block: ValidatedBlock,
  sessionId: string,
  suffix: string,
  startOrder: number,
): Array<Draft<Item>> {
  const course = block.course;
  const out: Array<Draft<Item>> = [];
  let order = startOrder;

  for (const f of block.flashcards) {
    out.push({
      id: f.id + suffix,
      deleted: false,
      course,
      sessionId,
      type: 'flashcard',
      category: f.category,
      difficulty: f.difficulty,
      order: order++,
      front: f.front,
      back: f.back,
      ...(f.flag ? { flag: f.flag } : {}),
      ...(f.flagText ? { flagText: f.flagText } : {}),
    });
  }

  for (const q of block.questions) {
    const base = {
      id: q.id + suffix,
      deleted: false,
      course,
      sessionId,
      category: q.category,
      difficulty: q.difficulty,
      order: order++,
      q: q.q,
      ...(q.note ? { note: q.note } : {}),
      ...(q.flag ? { flag: q.flag } : {}),
      ...(q.flagText ? { flagText: q.flagText } : {}),
    };
    const withRubric = q.rubric ? { ...base, rubric: q.rubric } : base;
    if (q.type === 'mc') {
      out.push({
        ...withRubric,
        type: 'mc',
        opts: q.opts,
        answerIndex: q.answerIndex,
        ...(q.sample ? { sample: q.sample } : {}),
      });
    } else if (q.type === 'free') {
      out.push({
        ...withRubric,
        type: 'free',
        parts: q.parts.map((p) => ({
          label: p.label,
          prompt: p.prompt,
          sample: p.sample,
          ...(p.rubric ? { rubric: p.rubric } : {}),
        })),
      });
    } else if (q.type === 'work') {
      out.push({ ...withRubric, type: 'work', steps: q.steps, answer: q.answer });
    } else {
      out.push({ ...withRubric, type: 'short', sample: q.sample });
    }
  }

  return out;
}

export function buildTemplates(
  block: ValidatedBlock,
  sessionId: string,
  suffix: string,
  startOrder: number,
): Array<Draft<ProblemTemplate>> {
  return block.problemTemplates.map((t, i) => ({
    id: t.id + suffix,
    deleted: false,
    course: block.course,
    sessionId,
    category: t.category,
    difficulty: t.difficulty,
    order: startOrder + i,
    prompt: t.prompt,
    variables: t.variables as ProblemTemplate['variables'],
    constraints: t.constraints,
    compute: t.compute,
    answerUnits: t.answerUnits,
    unitAliases: t.unitAliases,
    sigfigRule: t.sigfigRule,
    tolerance: t.tolerance,
    steps: t.steps,
  }));
}

export function buildReference(
  block: ValidatedBlock,
  sessionId: string,
  suffix: string,
): { formulas: Array<Draft<Formula>>; constants: Array<Draft<Constant>> } {
  return {
    formulas: block.formulas.map((f) => ({
      id: f.id + suffix,
      deleted: false,
      course: block.course,
      sessionId,
      name: f.name,
      expr: f.expr,
      topic: f.topic,
      ...(f.notes ? { notes: f.notes } : {}),
    })),
    constants: block.constants.map((c) => ({
      id: c.id + suffix,
      deleted: false,
      course: block.course,
      sessionId,
      name: c.name,
      symbol: c.symbol,
      value: c.value,
      ...(c.units ? { units: c.units } : {}),
    })),
  };
}

/** Finds a free `-2`, `-3`, … suffix for Keep both. */
export async function nextKeepBothSuffix(sessionId: string): Promise<string> {
  for (let n = 2; n < 100; n += 1) {
    const candidate = `${sessionId}-${n}`;
    const existing = await db.sessions.get(candidate);
    if (!existing) return `-${n}`;
  }
  return `-${Date.now()}`;
}

function sessionRecord(block: ValidatedBlock, id: string, suffixNote: string): Draft<Session> {
  const s = block.session as FullSessionInput;
  return {
    id,
    deleted: false,
    course: block.course,
    kind: s.kind,
    number: s.number ?? null,
    title: suffixNote ? `${s.title} (${suffixNote})` : s.title,
    shortLabel: suffixNote ? `${s.shortLabel} ${suffixNote}` : s.shortLabel,
    addedAt: s.addedAt,
  };
}

// ------------------------------------------------------------------ applying

export async function applyBlock(
  block: ValidatedBlock,
  decision: ImportDecision,
): Promise<ApplyResult> {
  if (decision === 'append') return applyAppend(block);
  if (decision === 'keep-both') return applyKeepBoth(block);
  if (decision === 'merge') return applyMerge(block);
  return applyCreateOrReplace(block, decision === 'create' ? 'create' : 'replace');
}

async function applyCreateOrReplace(
  block: ValidatedBlock,
  decision: 'create' | 'replace',
): Promise<ApplyResult> {
  const sessionId = block.sessionId;
  const items = buildItems(block, sessionId, '', 0);
  const templates = buildTemplates(block, sessionId, '', 0);
  const { formulas, constants } = buildReference(block, sessionId, '');

  await put<Session>('sessions', sessionRecord(block, sessionId, ''));
  await putMany<Item>('items', items);
  await putMany<ProblemTemplate>('templates', templates);
  await putMany<Formula>('formulas', formulas);
  await putMany<Constant>('constants', constants);

  // Anything that used to be in this session and is not in the new block is
  // tombstoned. Progress for surviving ids is untouched because progress is
  // keyed by item id in its own table.
  const incoming = new Set([
    ...items.map((i) => i.id),
    ...templates.map((t) => t.id),
    ...formulas.map((f) => f.id),
    ...constants.map((c) => c.id),
  ]);
  const stale = await staleIds(sessionId, incoming);
  await tombstoneMany('items', stale.items);
  await tombstoneMany('templates', stale.templates);
  await tombstoneMany('formulas', stale.formulas);
  await tombstoneMany('constants', stale.constants);

  return {
    sessionId,
    decision,
    itemsWritten: items.length,
    itemsRemoved: stale.items.length,
    templatesWritten: templates.length,
    referenceWritten: formulas.length + constants.length,
  };
}

async function staleIds(
  sessionId: string,
  incoming: Set<string>,
): Promise<{ items: string[]; templates: string[]; formulas: string[]; constants: string[] }> {
  const pick = async (table: 'items' | 'templates' | 'formulas' | 'constants') => {
    const rows = await db[table].where('sessionId').equals(sessionId).toArray();
    return rows.filter((r) => !r.deleted && !incoming.has(r.id)).map((r) => r.id);
  };
  return {
    items: await pick('items'),
    templates: await pick('templates'),
    formulas: await pick('formulas'),
    constants: await pick('constants'),
  };
}

/** Adds ids that are new and leaves every existing record exactly as it is. */
async function applyMerge(block: ValidatedBlock): Promise<ApplyResult> {
  const sessionId = block.sessionId;
  const maxOrder = await highestOrder(sessionId);

  const allItems = buildItems(block, sessionId, '', maxOrder + 1);
  const existingItemIds = new Set(
    (await db.items.where('sessionId').equals(sessionId).primaryKeys()) as string[],
  );
  const newItems = allItems.filter((i) => !existingItemIds.has(i.id));

  const allTemplates = buildTemplates(block, sessionId, '', 0);
  const existingTemplateIds = new Set(
    (await db.templates.where('sessionId').equals(sessionId).primaryKeys()) as string[],
  );
  const newTemplates = allTemplates.filter((t) => !existingTemplateIds.has(t.id));

  const { formulas, constants } = buildReference(block, sessionId, '');
  const existingFormulaIds = new Set((await db.formulas.toCollection().primaryKeys()) as string[]);
  const existingConstantIds = new Set((await db.constants.toCollection().primaryKeys()) as string[]);
  const newFormulas = formulas.filter((f) => !existingFormulaIds.has(f.id));
  const newConstants = constants.filter((c) => !existingConstantIds.has(c.id));

  // Re-number the survivors so they land after what is already there.
  newItems.forEach((item, i) => {
    item.order = maxOrder + 1 + i;
  });

  await putMany<Item>('items', newItems);
  await putMany<ProblemTemplate>('templates', newTemplates);
  await putMany<Formula>('formulas', newFormulas);
  await putMany<Constant>('constants', newConstants);

  return {
    sessionId,
    decision: 'merge',
    itemsWritten: newItems.length,
    itemsRemoved: 0,
    templatesWritten: newTemplates.length,
    referenceWritten: newFormulas.length + newConstants.length,
  };
}

async function applyKeepBoth(block: ValidatedBlock): Promise<ApplyResult> {
  const suffix = await nextKeepBothSuffix(block.sessionId);
  const sessionId = block.sessionId + suffix;
  const label = suffix.replace('-', '#');

  const items = buildItems(block, sessionId, suffix, 0);
  const templates = buildTemplates(block, sessionId, suffix, 0);
  const { formulas, constants } = buildReference(block, sessionId, suffix);

  await put<Session>('sessions', sessionRecord(block, sessionId, label));
  await putMany<Item>('items', items);
  await putMany<ProblemTemplate>('templates', templates);
  await putMany<Formula>('formulas', formulas);
  await putMany<Constant>('constants', constants);

  return {
    sessionId,
    decision: 'keep-both',
    itemsWritten: items.length,
    itemsRemoved: 0,
    templatesWritten: templates.length,
    referenceWritten: formulas.length + constants.length,
  };
}

/** Part 2+ of a multi-part session: append, no dialog, no replacement. */
async function applyAppend(block: ValidatedBlock): Promise<ApplyResult> {
  const sessionId = block.sessionId;
  const maxOrder = await highestOrder(sessionId);
  const items = buildItems(block, sessionId, '', maxOrder + 1);
  const templateCount = await db.templates.where('sessionId').equals(sessionId).count();
  const templates = buildTemplates(block, sessionId, '', templateCount);
  const { formulas, constants } = buildReference(block, sessionId, '');

  await putMany<Item>('items', items);
  await putMany<ProblemTemplate>('templates', templates);
  await putMany<Formula>('formulas', formulas);
  await putMany<Constant>('constants', constants);

  // Touch the session so sync sees the change.
  const session = await db.sessions.get(sessionId);
  if (session) await db.sessions.put({ ...session, updatedAt: nowMs() });

  return {
    sessionId,
    decision: 'append',
    itemsWritten: items.length,
    itemsRemoved: 0,
    templatesWritten: templates.length,
    referenceWritten: formulas.length + constants.length,
  };
}

async function highestOrder(sessionId: string): Promise<number> {
  const rows = await db.items.where('sessionId').equals(sessionId).toArray();
  return rows.reduce((max, r) => Math.max(max, r.order), -1);
}
